/**
 * Supabase Storage API for file uploads
 * Handles photo uploads for vehicle damage documentation
 */

import { supabase } from './supabase';
import { Platform } from 'react-native';

/**
 * Upload a damage photo to Supabase Storage
 * @param photoUri - Local URI of the photo (from ImagePicker)
 * @param formType - Type of form ('receiving', 'delivery', or 'job-card')
 * @returns Public URL of the uploaded photo
 */
export const uploadDamagePhoto = async (
  photoUri: string,
  formType: 'receiving' | 'delivery' | 'job-card' = 'receiving'
): Promise<string> => {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    const fileName = `damage_${formType}_${timestamp}_${random}.jpg`;

    // Convert URI to blob/file based on platform
    let fileData: Blob | File;
    
    if (Platform.OS === 'web') {
      // Web: fetch and convert to blob
      const response = await fetch(photoUri);
      fileData = await response.blob();
    } else {
      // Mobile: create FormData and extract blob
      const response = await fetch(photoUri);
      fileData = await response.blob();
    }

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('vehicle-damages') // Storage bucket name
      .upload(fileName, fileData, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload photo: ${error.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('vehicle-damages')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading damage photo:', error);
    throw error;
  }
};

/**
 * Delete a damage photo from Supabase Storage
 * @param photoUrl - Public URL of the photo to delete
 */
export const deleteDamagePhoto = async (photoUrl: string): Promise<void> => {
  try {
    // Extract filename from URL
    const urlParts = photoUrl.split('/');
    const fileName = urlParts[urlParts.length - 1];

    const { error } = await supabase.storage
      .from('vehicle-damages')
      .remove([fileName]);

    if (error) {
      console.error('Delete error:', error);
      throw new Error(`Failed to delete photo: ${error.message}`);
    }
  } catch (error) {
    console.error('Error deleting damage photo:', error);
    throw error;
  }
};

/**
 * Upload multiple damage photos
 * @param photoUris - Array of local photo URIs
 * @param formType - Type of form
 * @returns Array of public URLs
 */
export const uploadMultipleDamagePhotos = async (
  photoUris: string[],
  formType: 'receiving' | 'delivery' | 'job-card' = 'receiving'
): Promise<string[]> => {
  try {
    const uploadPromises = photoUris.map(uri => uploadDamagePhoto(uri, formType));
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple photos:', error);
    throw error;
  }
};

/**
 * Check if storage bucket exists, create if needed
 * Note: This requires admin privileges, run from Supabase dashboard SQL editor if needed
 */
export const ensureStorageBucketExists = async (): Promise<void> => {
  try {
    const { data, error } = await supabase.storage.getBucket('vehicle-damages');
    
    if (error && error.message.includes('not found')) {
      // Bucket doesn't exist - provide instructions
      console.warn(`
        Storage bucket 'vehicle-damages' not found.
        Please create it in Supabase Dashboard:
        1. Go to Storage
        2. Click "New bucket"
        3. Name: vehicle-damages
        4. Public: Yes (for easy access)
        5. Click Create
      `);
    }
  } catch (error) {
    console.error('Error checking storage bucket:', error);
  }
};
